import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { CheckboxGroups } from '~/eds/src/components/CheckboxGroups/CheckboxGroups';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useThrottledValue } from '~/eds/src/hooks/useThrottledValue';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { Nullable } from '~/types/Nullable';

import { Field, FieldId, Group, GroupId } from './types';
import { groupFieldOptions } from './utils';

interface Props {
  fields: Record<FieldId, Field>;
  groups: Group[];
  onAdd: (fieldIds: Record<GroupId, FieldId[]>) => void;
  pinnedFieldIds?: FieldId[];
  onUpdatePinnedFieldIds?: (
    updatedFieldIds: FieldId[],
    action: { value: FieldId; type: 'pin' | 'unpin' },
  ) => void;
}

export const AddFilters = ({
  fields,
  groups,
  pinnedFieldIds,
  onAdd,
  onUpdatePinnedFieldIds,
}: Props) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const user = useCurrentUser();
  const [selectedFieldIds, setSelectedFieldIds] = useState<
    Nullable<Record<GroupId, FieldId[]>>
  >({});
  const hasFieldsSelected =
    selectedFieldIds && Object.values(selectedFieldIds).flat().length > 0;

  // Determine throttleMs based on the data size.  Interim perf solution until CheckboxGroups can be virtualized via react-window.
  const throttleMs = useMemo(() => Object.keys(fields).length * 2, [fields]);
  const throttledSearch = useThrottledValue(search, throttleMs);

  const handleHide = () => {
    setSearch('');
    setSelectedFieldIds({});
  };

  const handleSearch = (updatedSearch: Nullable<string>) => {
    setSearch(updatedSearch || '');
  };

  const handleSubmit = () => {
    onAdd(selectedFieldIds || {});
    hideModal();
  };

  const handleShowFilters = () => {
    trackSegment('openFilters', {
      user_id: user.id,
      client_id: user.client,
    });
    showModal();
  };

  const checkboxGroups = React.useMemo(() => {
    const groupedFieldOptions = groupFieldOptions({ fields, groups });
    const optionsCount = groupedFieldOptions.reduce(
      (acc, group) => acc + group.options.length,
      0,
    );

    if (optionsCount === 0) {
      return (
        <Text preset="description">
          {intl.formatMessage({
            id: 'AddFilters.noMatchingFields',
            defaultMessage: 'No matching fields.',
            description: 'Message when no fields match the search',
          })}
        </Text>
      );
    }

    return (
      <CheckboxGroups
        columns={3}
        groups={groupedFieldOptions}
        pins={pinnedFieldIds}
        search={throttledSearch}
        value={selectedFieldIds}
        onChange={setSelectedFieldIds}
        onUpdatePins={onUpdatePinnedFieldIds}
      />
    );
  }, [fields, groups, pinnedFieldIds, selectedFieldIds, throttledSearch]);

  const [modal, showModal, hideModal] = useModal({
    children: (
      <Layout direction="column" h="100%" spacing={6}>
        <Box flex="none">
          <SearchInput
            autoFocus
            name="fields"
            placeholder={intl.formatMessage({
              id: 'AddFilters.searchFieldsPlaceholder',
              defaultMessage: 'Search fields',
              description: 'Placeholder text for field search input',
            })}
            value={search}
            onChange={handleSearch}
          />
        </Box>
        <Box flex="auto" overflowY="auto">
          {checkboxGroups}
        </Box>
      </Layout>
    ),
    disableHideOnEscape: true,
    isStretched: true,
    primaryAction: {
      disabled: !hasFieldsSelected,
      text: intl.formatMessage({
        id: 'AddFilters.addButtonText',
        defaultMessage: 'Add',
        description: 'Button text to add selected filters',
      }),
      onClick: handleSubmit,
    },
    onHide: handleHide,
    title: intl.formatMessage({
      id: 'AddFilters.modalTitle',
      defaultMessage: 'Add Filters',
      description: 'Title for add filters modal',
    }),
    width: 'l',
  });

  return (
    <>
      <Button
        text={intl.formatMessage({
          id: 'AddFilters.addFiltersButtonText',
          defaultMessage: 'Add Filters',
          description: 'Button text to open add filters modal',
        })}
        variant="action"
        onClick={handleShowFilters}
      />
      {modal}
    </>
  );
};
